import {
  FaCircle,
  FaComment,
  FaCommentSlash,
  FaPaperclip,
  FaUser,
} from "react-icons/fa";
import styled from "styled-components";
import { useChat } from "../../../hooks/useChat";
import { useRemoteParticipant } from "../../../hooks/useRemoteParticipant";
import ActionRowButton from "../../Buttons/ActionRowButton";
import { useInfo } from "../../../hooks/useInfo";
import { getAllowParticipants } from "../../../api/rooms/getAllowParticipant";
import { useState, useEffect } from "react";
import useQuery from "../../../utils/useQuery";

const Wrapper = styled("div") <{ spacing: number }>`
  display: flex;
  align-items: center;
  gap: ${(props) => props.spacing}px;
`;

const AppendButtonWrapper = styled.div`
  @container (max-width: 560px) {
    display: none;
  }
`;

const ChatBadge = styled(FaCircle)`
  @keyframes pulse {
    from {
      top: 8px;
      right: 8px;
      width: 12px;
    }
    to {
      top: 9px;
      right: 9px;
      width: 10px;
    }
  }

  position: absolute;
  fill: rgb(226, 38, 38) !important;
  height: auto;
  animation-name: pulse;
  animation-duration: 0.3s;
  animation-direction: alternate;
  animation-delay: 0.2s;
  animation-iteration-count: infinite;
`;

interface IChatButtonsProps {
  buttonsSpacing: number;
  onToggleWaitingList: () => void;
}

interface WaitingPatient {
  patientId: number;
  name: string;
}

const ChatButtons = ({ buttonsSpacing = 6, onToggleWaitingList }: IChatButtonsProps) => {
  const { notification, chatOpen, fileInputRef, handleChatOpen } = useChat();
  const { remoteParticipant } = useRemoteParticipant();
  const { telemedicineData } = useInfo();
  const [participants, setParticipants] = useState<WaitingPatient[]>([]);
  const [loading, setLoading] = useState(true);
  const [forceTooltip, setForceTooltip] = useState(false);

  const query = useQuery();
  const isWhitelabel = query.get("whitelabel") === "true";

  const handleAppendFiles = () => {
    handleChatOpen(true);
    if (fileInputRef && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleChatButton = () => {
    handleChatOpen(!chatOpen);
  };

  const fetchWaitingPatients = async () => {
    try {
      const data = await getAllowParticipants(telemedicineData.id);
      setParticipants(data.patients || []);
    } catch (err) {
      console.error("Erro ao buscar pacientes aguardando:", err);
      setParticipants([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWaitingPatients();
    const interval = setInterval(fetchWaitingPatients, 3000);
    return () => clearInterval(interval);
  }, []);

  const tooltipWaitingList = participants.length > 0
    ? "Participantes aguardando acesso à sala de atendimento"
    : "Lista de Participantes";

  useEffect(() => {
    setForceTooltip(participants.length > 0);
  }, [participants]);

  return (
    <Wrapper spacing={buttonsSpacing}>
      {remoteParticipant && (
        <AppendButtonWrapper>
          <ActionRowButton
            tooltip={chatOpen ? "" : "Anexar arquivos"}
            disabled={chatOpen}
            handleClick={handleAppendFiles}
            Icon={FaPaperclip}
          />
        </AppendButtonWrapper>
      )}
      <ActionRowButton
        tooltip={chatOpen ? "Fechar chat" : "Abrir chat"}
        handleClick={handleChatButton}
        Icon={chatOpen ? FaCommentSlash : FaComment}
        iconSize={chatOpen ? undefined : 23}
      >
        {notification && !chatOpen && <ChatBadge />}
      </ActionRowButton>
      {telemedicineData.permission === "professional" && telemedicineData.telemedicineConfiguration.sala_espera && (
        <ActionRowButton
          key={forceTooltip ? 'with-tooltip' : 'no-tooltip'}
          tooltip={tooltipWaitingList}
          handleClick={() => { onToggleWaitingList() }}
          Icon={FaUser}
          forceTooltip={forceTooltip}
        />
      )}
    </Wrapper>
  );
};

export default ChatButtons;
